import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PawPrint, ArrowRight, Cookie, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";
import { useCookieConsent } from "@/context/CookieConsentContext";
import { TOP_CITIES, CITIES } from "@/data/cities";

const PRODUCT_LINKS = [
  { label: "Hundpensionat", to: "/hundpensionat-system" },
  { label: "Hunddagis", to: "/hunddagis-app" },
  { label: "Hundrastning", to: "/hundrastning-program" },
  { label: "Priser", to: "/pricing" },
];

const COMPANY_LINKS = [
  { label: "Om oss", to: "/om-oss", testid: "footer-link-about" },
  { label: "Karriär", to: "/om-oss" },
  { label: "Press", to: "/om-oss" },
  { label: "Kontakt", to: "/om-oss" },
];

const RESOURCE_LINKS = [
  { label: "Guider", to: "/" },
  { label: "Webinars", to: "/" },
  { label: "Hjälpcenter", to: "/" },
  { label: "Status", to: "/" },
];

const LEGAL_LINKS = [
  { label: "Integritetspolicy", to: "/integritetspolicy", testid: "footer-link-privacy" },
  { label: "Villkor", to: "/villkor", testid: "footer-link-terms" },
  { label: "Cookies", to: "/cookies", testid: "footer-link-cookies" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const { openPreferences } = useCookieConsent();

  const submit = (e) => {
    e.preventDefault();
    if (!email.includes("@")) return toast.error("Ange en giltig e-postadress");
    toast.success("Tack! Du är prenumererad.");
    setEmail("");
  };

  return (
    <footer data-testid="footer" className="relative bg-slate-950 text-slate-300">
      <div className="absolute inset-0 bg-dotted opacity-30 pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-12 py-20">
        {/* CTA band */}
        <div
          data-testid="footer-cta"
          className="rounded-3xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white p-10 md:p-14 mb-20 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-dotted opacity-20" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-display text-3xl md:text-4xl font-bold leading-tight">
                Redo att förenkla din vardag?
              </h3>
              <p className="mt-3 text-emerald-50/90 max-w-md">
                Kom igång på 5 minuter. Gratis i 14 dagar — inget kreditkort krävs.
              </p>
            </div>
            <form
              onSubmit={submit}
              className="flex flex-col sm:flex-row gap-3 md:justify-end"
            >
              <Input
                data-testid="footer-newsletter-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="din@epost.se"
                className="h-12 rounded-xl bg-white/10 border-white/20 placeholder:text-white/60 text-white focus-visible:ring-white/30"
              />
              <Button
                data-testid="footer-newsletter-submit"
                type="submit"
                className="h-12 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold whitespace-nowrap px-6"
              >
                Starta gratis <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </form>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-10">
          <div className="col-span-2 md:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-5 group">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500 text-white transition-transform group-hover:rotate-6">
                <PawPrint className="h-5 w-5" />
              </div>
              <span className="font-display text-2xl font-bold text-white">Hundify</span>
            </Link>
            <p className="text-sm leading-relaxed text-slate-400 max-w-xs">
              Det smarta affärssystemet för hundpensionat, hunddagis och hundpromenader.
              Byggt i Sverige.
            </p>

            {/* Local SEO links */}
            <div
              data-testid="footer-local-cities"
              className="mt-7"
            >
              <h4 className="font-display text-[11px] font-semibold text-white uppercase tracking-[0.2em] mb-3 inline-flex items-center gap-1.5">
                <MapPin className="h-3 w-3 text-emerald-500" />
                Hundify i Sverige
              </h4>
              <ul className="flex flex-wrap gap-x-4 gap-y-2">
                {TOP_CITIES.map((slug) => {
                  const c = CITIES[slug];
                  return (
                    <li key={slug}>
                      <Link
                        to={`/lokalt/${slug}`}
                        data-testid={`footer-city-${slug}`}
                        className="text-sm text-slate-400 hover:text-emerald-400 transition-colors"
                      >
                        {c.name}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          <FooterCol title="Produkt" links={PRODUCT_LINKS} />
          <FooterCol title="Företag" links={COMPANY_LINKS} />
          <FooterCol title="Resurser" links={RESOURCE_LINKS} />
          <FooterCol title="Juridik" links={LEGAL_LINKS} />
        </div>

        <div className="mt-16 pt-8 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <div>© {new Date().getFullYear()} Hundify · Stockholm, Sverige</div>
          <nav
            data-testid="footer-legal-nav"
            className="flex flex-wrap items-center gap-x-6 gap-y-2"
          >
            {LEGAL_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`${l.testid}-bottom`}
                className="hover:text-slate-300 transition-colors"
              >
                {l.label}
              </Link>
            ))}
            <button
              type="button"
              onClick={openPreferences}
              data-testid="footer-cookie-settings"
              className="inline-flex items-center gap-1.5 hover:text-slate-300 transition-colors"
            >
              <Cookie className="h-3.5 w-3.5" />
              Cookieinställningar
            </button>
          </nav>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <h4 className="font-display text-sm font-semibold text-white uppercase tracking-wider mb-4">
        {title}
      </h4>
      <ul className="space-y-3">
        {links.map((l) => (
          <li key={`${title}-${l.label}`}>
            <Link
              to={l.to}
              data-testid={l.testid}
              className="text-sm text-slate-400 hover:text-emerald-400 transition-colors"
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
